// Single source of truth for route paths + run-status enum + kit-class lookups.
// Names are camelCase per @typescript-eslint/naming-convention (UPPER_SNAKE
// fails lint at module scope).
export const routes = {
  HOME: '/',
  /**
   * Chat-first landing surface (DEV-16971). The console lands here on login and
   * when the topbar logo ("Home") is clicked. `HOME` stays `/` — it's the OIDC
   * `redirect_uri` root and the `/` index route, which redirects here.
   */
  CHAT: '/chat',
  chatThread: (id: string) => `/chat/${id}`,
  WORKFLOWS: '/workflows',
  workflowDetail: (id: string) => `/workflows/${id}`,
  /** Incident Board view of the unified Inbox (was `/triage/runs`). */
  INBOX_BOARD: '/inbox/board',
  /** Coordinator view of the unified Inbox — the live dedup stage (was `/orchestrator/live`). */
  INBOX_COORDINATOR: '/inbox/coordinator',
  /** AI-managed dashboards (agent/PAT-authored widgets, console-rendered). */
  DASHBOARDS: '/dashboards',
  dashboardDetail: (id: string) => `/dashboards/${id}`,
  /** Legacy run deep-link — redirects into the board with `?runId=`. */
  runDetail: (id: string) => `/triage/runs/${id}`,
  hitlDetail: (runId: string) => `/inbox/hitl/${runId}`,
  REFINEMENT: '/refinement/history',
  REFINEMENT_OVERVIEW: '/refinement/overview',
  REFINEMENT_SCHEDULES: '/refinement/schedules',
  /** Org-wide workflow schedules (DEV-17470), Automation > Schedules. */
  WORKFLOW_SCHEDULES: '/schedules',
  /** @deprecated /refinement/memories is deleted — use routes.MEMORY */
  REFINEMENT_MEMORIES: '/agents/memories',
  MEMORY_METRICS: '/agents/memories/metrics',
  MEMORY_DIGEST: '/agents/memories/digest',
  /** Unified Agents page — agents + skills + memories as internal views. */
  AGENTS: '/agents',
  AGENTS_SESSIONS: '/agents/sessions',
  AGENTS_NEW: '/agents/new',
  agentDetail: (id: string) => `/agents/${id}`,
  /** Orchestration — persistent agents, under Automation > Agents (flags.persistentAgentOrchestration). */
  ORCHESTRATION: '/agents/orchestration',
  /** Orchestration graph with a thread focused in the interaction panel. */
  orchestrationFocusedThread: (threadId: string) =>
    `/agents/orchestration?focusedThreadId=${encodeURIComponent(threadId)}`,
  ORCHESTRATION_BENCHMARKS: '/agents/orchestration/benchmarks',
  orchestrationBenchmark: (id: string) => `/agents/orchestration/benchmarks/${id}`,
  ORCHESTRATION_FILESYSTEMS: '/agents/orchestration/filesystems',
  ORCHESTRATION_SIGNALS: '/agents/orchestration/signals',
  orchestrationFilesystem: (id: string) => `/agents/orchestration/filesystems/${id}`,
  ORCHESTRATION_SPECS: '/agents/orchestration/specs',
  orchestrationSpec: (id: string) => `/agents/orchestration/specs/${id}`,
  orchestrationThread: (agentId: string, threadId: string) =>
    `/agents/${agentId}/threads/${threadId}`,
  MODELS: '/models',
  MODELS_NEW: '/models/new',
  AGENTS_ASSISTANT: '/agents/assistant',
  modelDetail: (id: string) => `/models/${id}`,
  /** Platform Costs — cost, usage, and execution detail over trace_span. */
  PLATFORM_COSTS: '/traces',
  /** @deprecated Use PLATFORM_COSTS; the legacy URL remains `/traces`. */
  TRACES: '/traces',
  /** Flame-graph / waterfall view of one trace. */
  traceDetail: (traceId: string) => `/traces/${traceId}`,
  EVALS: '/evals',
  EVALS_NEW: '/evals/new',
  evalDetail: (id: string) => `/evals/${id}`,
  /**
   * Per-eval node-level surface — both authoring the workflow's node
   * ground truth AND viewing this eval's per-node scoring in one
   * focused page. Sibling of `evalDetail` (the workflow-level surface).
   */
  evalNodes: (id: string) => `/evals/${id}/nodes`,
  JUDGES: '/judges',
  JUDGES_NEW: '/judges/new',
  judgeDetail: (id: string) => `/judges/${id}`,
  TASKS: '/tasks',
  TASKS_NEW: '/tasks/new',
  taskDetail: (id: string) => `/tasks/${id}`,
  SUITES: '/suites',
  SUITES_NEW: '/suites/new',
  suiteDetail: (id: string) => `/suites/${id}`,
  FEEDBACK: '/feedback',
  GROUND_TRUTH: '/ground-truth',
  GROUND_TRUTH_NEW: '/ground-truth/new',
  GROUND_TRUTH_BULK: '/ground-truth/bulk',
  BOARD_COLUMNS: '/board-columns',
  BOARD_COLUMNS_NEW: '/board-columns/new',
  boardColumnDetail: (id: string) => `/board-columns/${id}`,
  MCP_CONNECTIONS: '/mcp-connections',
  APP_CONNECTIONS: '/app-connections',
  /** Personal access tokens for headless MCP-server auth. */
  API_KEYS: '/api-keys',
  DOCUMENT_SETS: '/document-sets',
  documentSetDetail: (id: string) => `/document-sets/${id}`,
  USERS: '/users',
  /** Operator admin for the Signal Orchestrator (DEV-16226). */
  ORCHESTRATOR: '/orchestrator',
  /** @deprecated the live view moved to routes.INBOX_COORDINATOR — old URL redirects. */
  ORCHESTRATOR_LIVE: '/orchestrator/live',
  /** Workflow-run analytics with dedup overlay (DEV-16231). */
  ORCHESTRATOR_ANALYTICS: '/orchestrator/analytics',
  /** Unified Inbox — HITL queue + open signals; Board + Coordinator live as internal views. */
  INBOX: '/inbox',
  /** Closed Signals tab (DEV-16232). */
  INBOX_CLOSED_SIGNALS: '/inbox/closed-signals',
  /** Legacy signal detail URL — redirects into the Inbox drawer (DEV-16229). */
  signalDetail: (id: string) => `/signals/${id}`,
  INTERNAL_REMOTE_REALTIME_TOOLS: '/internal/remote-realtime-tools',
  /** Skills view of the unified Agents page (was `/skills`). */
  SKILLS: '/agents/skills',
  /** Memories view of the unified Agents page (was `/memory`). */
  MEMORY: '/agents/memories',
  /** Agent collections — group binding (DEV-16997). */
  COLLECTIONS: '/agents/collections',
  /** Per-target memory auto-approve policies (DEV-16823). */
  AUTO_APPROVE: '/agents/auto-approve',
  SETTINGS: '/settings',
  INTERNAL: '/internal',
  INTERNAL_ORGS: '/internal/organizations',
  INTERNAL_ORGS_NEW: '/internal/organizations/new',
  internalOrgDetail: (id: string) => `/internal/organizations/${id}`,
  INTERNAL_PIPELINES: '/internal/pipelines',
  INTERNAL_PIPELINES_NEW: '/internal/pipelines/new',
  internalPipelineDetail: (id: string) => `/internal/pipelines/${id}`,
  KNOWLEDGE_GRAPHS: '/knowledge-graphs',
  INTERNAL_KNOWLEDGE_GRAPHS: '/internal/knowledge-graphs',
  INTERNAL_BLUEPRINTS: '/internal/blueprints',
  INTERNAL_BLUEPRINTS_NEW: '/internal/blueprints/new',
  internalBlueprintDetail: (id: string) => `/internal/blueprints/${id}`,
} as const;

/** Run status values (matches RunStatusSchema in @cortex/types) in kanban left → right order. */
export const runStatuses = [
  'PENDING',
  'RUNNING',
  'PAUSED',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'ABORTED',
] as const;

export type RunStatusKey = (typeof runStatuses)[number];

export const runStatusBadge: Record<RunStatusKey, string> = {
  PENDING: 'fk-badge--pending',
  RUNNING: 'fk-badge--running',
  PAUSED: 'fk-badge--input-required',
  COMPLETED: 'fk-badge--completed',
  FAILED: 'fk-badge--failed',
  CANCELLED: 'fk-badge--rejected',
  ABORTED: 'fk-badge--rejected',
};

export const runStatusLabel: Record<RunStatusKey, string> = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  PAUSED: 'Awaiting input',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  ABORTED: 'Aborted',
};

// `runStatusAccent` (status → kanban header class) lived here. Its only
// consumer was the board's status mode, which DEV-17093 removed — the board
// no longer has a column per RunStatus. Nothing replaced it: the synthetic
// lanes that took its place (Pending / Awaiting input / In progress /
// Finished) render deliberately neutral, so color on the board means "this
// column was configured by someone". Status still reads as color on the card
// badge via `runStatusBadge` above.

/** Roles available for users within an org (matches @cortex/types UserRoleSchema). */
export const userRoles = ['ADMIN', 'EDITOR', 'VIEWER'] as const;
export type UserRoleKey = (typeof userRoles)[number];

/** Run-state buckets that mean "won't change again" — use to stop polling/SSE. */
export const terminalRunStatuses: ReadonlySet<RunStatusKey> = new Set([
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'ABORTED',
]);
